// StickyHeader.jsx
import { useEffect, useRef, useState } from "react";
import { useScrollSpy } from "../../hooks/useScrollSpy";
import "./header.css";

const SECTION_IDS = ["home", "about", "process", "skills", "projects", "contact"];

const StickyHeader = () => {
  const headerRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [locale, setLocale] = useState("en");
  const activeId = useScrollSpy(SECTION_IDS);
  const isPt = locale === "pt";
  const navLabels = {
    home: isPt ? "Inicio" : "Home",
    about: isPt ? "Sobre" : "About",
    skills: isPt ? "Habilidades" : "Skills",
    projects: isPt ? "Projetos" : "Projects",
    contact: isPt ? "Contato" : "Contact",
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem("locale");
    const next = stored === "pt" || stored === "en" ? stored : "en";
    setLocale(next);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("locale", locale);
    if (document?.documentElement) {
      document.documentElement.lang = locale;
      document.documentElement.setAttribute("data-locale", locale);
    }
    window.dispatchEvent(new CustomEvent("app:locale", { detail: locale }));
  }, [locale]);

  // sticky ao rolar
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      headerRef.current?.classList.toggle("is-sticky", y > 8);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // visibilidade: mostrar apenas do #home para baixo
  useEffect(() => {
    const headerEl = headerRef.current;
    const home = document.getElementById("home");
    if (!headerEl || !home) return;

    const compute = () => {
      const headerH = headerEl.offsetHeight || 0;
      const threshold = (home.getBoundingClientRect().top + window.scrollY) - headerH;
      const y = window.scrollY || 0;
      const visible = y >= threshold - 4; // pequena folga
      headerEl.classList.toggle("is-visible", visible);
    };

    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, []);

  // scroll suave com offset do header
  const handleNavClick = (e) => {
    const anchor = e.target.closest("a[href^='#']");
    if (!anchor) return;

    const hash = anchor.getAttribute("href");
    if (!hash || hash === "#") return;

    const target = document.querySelector(hash);
    if (!target) return;

    e.preventDefault(); // impede o salto instantâneo

    // altura do header fixo
    const headerH = headerRef.current?.offsetHeight || 0;

    // posição correta compensando o header
    const targetTop =
      target.getBoundingClientRect().top + window.scrollY - headerH - 8;

    // scroll animado
    window.scrollTo({
      top: targetTop,
      behavior: "smooth",
    });

    // atualiza a URL sem "pular"
    window.history.replaceState(null, "", hash);

    // fecha o menu mobile
    setNavOpen(false);
  };

  const toggleLocale = () => {
    setLocale((prev) => (prev === "en" ? "pt" : "en"));
  };

  return (
    <header className="s-header" ref={headerRef} onClick={handleNavClick}>
      <div className="s-header__inner">
        <div className="s-header__brand">
          <a
            className="s-header__logo"
            href="#home"
            aria-label={isPt ? "Voltar ao inicio" : "Back to top"}
          >
            AD
          </a>

          <button
            className="s-header__menu-toggle"
            aria-label={isPt ? "Abrir menu" : "Open menu"}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <span>Menu</span>
          </button>
        </div>

        <div className="s-header__actions">
          <nav
            className={`s-header__nav ${navOpen ? "is-open" : ""}`}
          >
            <ul className="s-header__menu">
              <li className={activeId === "home" ? "is-active" : ""}>
                <a
                  href="#home"
                  aria-current={activeId === "home" ? "page" : undefined}
                >
                  {navLabels.home}
                </a>
              </li>
              <li className={activeId === "about" ? "is-active" : ""}>
                <a
                  href="#about"
                  aria-current={activeId === "about" ? "page" : undefined}
                >
                  {navLabels.about}
                </a>
              </li>
              {/* <li className={activeId === "process" ? "is-active" : ""}>
                <a
                  href="#process"
                  aria-current={activeId === "process" ? "page" : undefined}
                >
                  Processo
                </a>
              </li> */}
              <li className={activeId === "skills" ? "is-active" : ""}>
                <a
                  href="#skills"
                  aria-current={activeId === "skills" ? "page" : undefined}
                >
                  {navLabels.skills}
                </a>
              </li>
              <li className={activeId === "projects" ? "is-active" : ""}>
                <a
                  href="#projects"
                  aria-current={activeId === "projects" ? "page" : undefined}
                >
                  {navLabels.projects}
                </a>
              </li>
              <li className={activeId === "contact" ? "is-active" : ""}>
                <a
                  href="#contact"
                  aria-current={activeId === "contact" ? "page" : undefined}
                >
                  {navLabels.contact}
                </a>
              </li>
            </ul>
          </nav>
          <button
            type="button"
            className="s-header__lang"
            aria-label={isPt ? "Mudar idioma" : "Change language"}
            onClick={toggleLocale}
            data-lang={locale}
          >
            {locale.toUpperCase()}
          </button>
        </div>
      </div>
    </header>
  );
};

export default StickyHeader;
